import { useState, useCallback, useEffect } from 'react';

const MAX_HISTORY = 40;

export function useBuilderHistory(initialState) {
  const [past, setPast] = useState([]);
  const [present, setPresent] = useState(initialState);
  const [future, setFuture] = useState([]);

  // Reset history (e.g. when loading a new page)
  const resetHistory = useCallback((newState) => {
    setPast([]);
    setPresent(newState);
    setFuture([]);
  }, []);

  // Update present document and push previous present to past
  const setDocument = useCallback((action) => {
    setPresent((currentPresent) => {
      const nextState = typeof action === 'function' ? action(currentPresent) : action;
      // If no change, return current
      if (JSON.stringify(currentPresent) === JSON.stringify(nextState)) {
        return currentPresent;
      }
      setPast((prevPast) => {
        const updated = [...prevPast, currentPresent];
        return updated.length > MAX_HISTORY ? updated.slice(updated.length - MAX_HISTORY) : updated;
      });
      setFuture([]);
      return nextState;
    });
  }, []);

  const undo = useCallback(() => {
    if (past.length === 0) return;
    const previous = past[past.length - 1];
    const newPast = past.slice(0, past.length - 1);

    setFuture((prevFuture) => [present, ...prevFuture]);
    setPresent(previous);
    setPast(newPast);
  }, [past, present]);

  const redo = useCallback(() => {
    if (future.length === 0) return;
    const next = future[0];
    const newFuture = future.slice(1);

    setPast((prevPast) => [...prevPast, present]);
    setPresent(next);
    setFuture(newFuture);
  }, [future, present]);

  // Global Keyboard Shortcuts (Ctrl+Z, Ctrl+Y, Ctrl+Shift+Z)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore when user is actively typing in an input or textarea
      const tag = e.target.tagName?.toLowerCase();
      const isInput = tag === 'input' || tag === 'textarea' || e.target.isContentEditable;
      if (isInput) return;

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const isCmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;

      if (isCmdOrCtrl && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
      } else if (isCmdOrCtrl && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  return {
    document: present,
    setDocument,
    resetHistory,
    undo,
    redo,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
    historyDepth: past.length,
  };
}
